import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, 
  Heart,
  Users,
  Star,
  CheckCircle2,
  ArrowRight,
  ChevronLeft
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { socialProjectsData } from '../data/mock';

const SocialDetailPage = () => {
  const { projectId } = useParams();
  const { t, language } = useLanguage();
  const [scrollY, setScrollY] = useState(0);

  const project = socialProjectsData.find(p => p.id === projectId);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [project]);

  if (!project) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-light text-white mb-4">
            {language === 'en' ? 'Project Not Found' : language === 'ar' ? 'المشروع غير موجود' : 'پڕۆژە نەدۆزرایەوە'}
          </h1>
          <Link to="/projects" className="text-white/60 hover:text-white transition-colors">
            {language === 'en' ? 'Return to Projects' : language === 'ar' ? 'العودة إلى المشاريع' : 'گەڕانەوە بۆ پڕۆژەکان'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="social-detail-page">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        {/* Background Image with Parallax */}
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-100"
          style={{ 
            backgroundImage: `url(${project.heroImage})`,
            transform: `translateY(${scrollY * 0.3}px) scale(1.1)`
          }}
        />
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
        
        {/* Back Button */}
        <Link 
          to="/projects" 
          className="absolute top-8 left-8 z-30 flex items-center gap-2 text-white/70 hover:text-white transition-colors group"
        >
          <ChevronLeft className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm uppercase tracking-widest font-light">
            {language === 'en' ? 'Back to Projects' : language === 'ar' ? 'العودة إلى المشاريع' : 'گەڕانەوە بۆ پڕۆژەکان'}
          </span>
        </Link>

        {/* Hero Content */}
        <div className="absolute inset-0 z-20 flex flex-col justify-end">
          <div className="container mx-auto px-6 lg:px-12 pb-16 max-w-7xl">
            {/* Status Badge */}
            <div className="mb-6 animate-fade-in-up">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black text-xs uppercase tracking-widest font-light">
                <Heart className="w-3 h-3" />
                {t('social.completed')}
              </span>
            </div>

            {/* Project Logo */}
            {project.logo && (
              <div className="mb-6 animate-fade-in-up animation-delay-100">
                <img
                  src={project.logo}
                  alt={project.name[language] || project.name.en}
                  className="h-24 w-24 object-contain rounded-lg"
                />
              </div>
            )}
            
            {/* Project Name */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-4 animate-fade-in-up animation-delay-200">
              {project.name[language] || project.name.en}
            </h1>
            
            {/* Location */}
            <div className="flex items-center gap-2 text-white/60 animate-fade-in-up animation-delay-300">
              <MapPin className="w-4 h-4" />
              <span className="text-sm uppercase tracking-widest font-light">
                {project.location[language] || project.location.en}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="scroll-reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light">
              {t('social.projectOverview')}
            </p>
            <p className="text-xl sm:text-2xl text-white/80 font-light leading-relaxed">
              {project.overview[language] || project.overview.en}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="scroll-reveal-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 mb-6">
                <Heart className="w-4 h-4 text-white/60" />
                <span className="text-xs uppercase tracking-widest font-light text-white/60">
                  {t('social.ourMission')}
                </span>
              </div>
              <p className="text-lg sm:text-xl text-white/70 font-light leading-relaxed">
                {project.mission[language] || project.mission.en}
              </p>
            </div>
            <div className="scroll-reveal-right">
              <img
                src={project.heroImage}
                alt="Mission"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-center mb-16 scroll-reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 mb-6">
              <Users className="w-4 h-4 text-white/60" />
              <span className="text-xs uppercase tracking-widest font-light text-white/60">
                {t('social.ourServices')}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight">
              {t('social.ourServices')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(project.services[language] || project.services.en).map((service, index) => (
              <div 
                key={index}
                className="scroll-reveal-scale p-6 bg-[#111] border border-white/10 hover:border-white/20 transition-colors"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CheckCircle2 className="w-6 h-6 text-white/60 mb-4" />
                <p className="text-white/70 font-light text-sm leading-relaxed">
                  {service}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-center mb-16 scroll-reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 mb-6">
              <Star className="w-4 h-4 text-white/60" />
              <span className="text-xs uppercase tracking-widest font-light text-white/60">
                {t('social.ourValues')}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight">
              {t('social.ourValues')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(project.values[language] || project.values.en).map((value, index) => (
              <div 
                key={index}
                className="scroll-reveal text-center"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-white/10 rounded-full flex items-center justify-center">
                  <Star className="w-6 h-6 text-white/60" />
                </div>
                <h3 className="text-lg font-light text-white mb-3 tracking-wide">
                  {value.title}
                </h3>
                <p className="text-white/50 font-light text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-center mb-16 scroll-reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 mb-6">
              <Heart className="w-4 h-4 text-white/60" />
              <span className="text-xs uppercase tracking-widest font-light text-white/60">
                {t('social.ourImpact')}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight">
              {t('social.ourImpact')}
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {(project.impact[language] || project.impact.en).map((item, index) => (
              <div 
                key={index}
                className="scroll-reveal flex items-start gap-4 mb-6 p-6 bg-[#111] border border-white/10"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CheckCircle2 className="w-6 h-6 text-white/60 flex-shrink-0 mt-0.5" />
                <p className="text-white/70 font-light leading-relaxed">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <div className="text-center scroll-reveal">
            <p className="text-xl sm:text-2xl text-white/70 font-light leading-relaxed italic">
              "{project.conclusion[language] || project.conclusion.en}"
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight mb-8 scroll-reveal">
            {language === 'en' ? 'Support Our Mission' : language === 'ar' ? 'ادعم مهمتنا' : 'پشتیوانی ئەرکمان بکە'}
          </h2>
          <p className="text-white/50 font-light mb-10 max-w-2xl mx-auto scroll-reveal">
            {language === 'en' 
              ? 'Join us in making a difference in the lives of our elderly community members.'
              : language === 'ar'
              ? 'انضم إلينا في إحداث فرق في حياة أفراد مجتمعنا من كبار السن.'
              : 'پێکەوە کاریگەری لە ژیانی ئەندامانی بەساڵاچووی کۆمەڵگەمان دروست بکەین.'}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-sm uppercase tracking-widest font-light hover:bg-white/90 transition-colors scroll-reveal"
          >
            <span>{t('cta.button')}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SocialDetailPage;
